import { useRef, useState, useEffect } from 'react'
import {
  ScrollView,
  TextInput,
  View,
  Text,
  type TextProps,
  StyleSheet,
  Platform,
} from 'react-native'
import { BackButton } from '@/src/common/components/BackButton'
import { Button } from '@/src/common/components/Button'
import { Routes } from '@/src/common/enums/routes'
import { Input } from '@/src/common/components/Input'
import { InputSsn } from '@/src/common/components/Input/InputSsn'
import {
  PiiInformationStore,
  usePiiInformationStore,
} from '@/src/common/store/usePiiInformationStore'
import { formatCurrency } from '@/src/common/utils/general'
import { useRouter } from 'expo-router'
import { BirthdateInput } from '@/src/common/components/Input/InputBirthdate'
import { useSardine } from '@/src/services/Sardine'
import { sardineIdKey } from '@/src/services/Sardine/SardineId'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { mesaColorNames, typography, fontFamilies } from '@/src/common/theme'
import { cardApplicationStyles as common } from './common'
import * as E2E from '../../e2e'
import { z } from 'zod/v4'

function useNumericStringState() {
  const [state, setState] = useState('')

  return [state, (text: typeof state) => setState(text.replace(/[^0-9]/g, ''))] as const
}

export function LastQuestion() {
  const router = useRouter()
  const [ssn, setSsn] = useState('')
  const [dob, setDob] = useState<Date>()
  const [totalAnnualIncome, setTotalAnnualIncome] = useNumericStringState()
  const [monthlyHousingPayment, setMonthlyHousingPayment] = useNumericStringState()
  const [errorsUnflattened, setErrors] =
    useState<ReturnType<typeof PiiInformationStore.safeParse>['error']>()
  const errors = errorsUnflattened ? z.flattenError(errorsUnflattened) : undefined
  const { setUserPiiInformation } = usePiiInformationStore()
  const ssnInputRef = useRef<TextInput>(null)
  const totalAnnualIncomeInputRef = useRef<TextInput>(null)
  const monthlyHousingPaymentInputRef = useRef<TextInput>(null)
  const { trackTextChange, trackFocusChange, updateOptions } = useSardine()

  useEffect(() => {
    void updateOptions({ flow: 'application-last-question' })
  }, [updateOptions])

  function checkForErrors(piiInformationStore?: Partial<PiiInformationStore>) {
    const result = PiiInformationStore.safeParse({
      ssn,
      dob,
      totalAnnualIncome,
      monthlyHousingPayment,
      ...piiInformationStore,
    })
    setErrors(result.error)
    return result
  }

  const [showErrors, setShowErrors] = useState(false)

  function onContinuePress() {
    setShowErrors(true)
    const result = checkForErrors()
    if (result.success) {
      setUserPiiInformation(result.data)
      router.push(Routes.ApplicationConfirmation.route)
    }
  }

  return (
    <CardApplicationWrapper>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={Platform.OS === 'web' && { overflow: 'visible' }}
        contentContainerStyle={{ gap: 10 }}
      >
        <BackButton
          href={Routes.FindYourHome.route}
          text="Back"
          testID={E2E.Config.Ids.Press.LastQuestionsBack}
        />
        <View style={common.headerText}>
          <Text style={typography['heading/heading2']}>Last questions</Text>
        </View>
        <Text style={[typography['paragraph/md'], common.instructionText]}>
          Then we're ready to review
        </Text>
        <BirthdateInput
          value={dob}
          onChange={(date) => {
            setDob(date)
            void trackTextChange('dateOfBirth', date?.toISOString() ?? '')
            showErrors && checkForErrors({ dob: date })
          }}
          onFocus={() => void trackFocusChange('dateOfBirth', true)}
          onBlur={() => void trackFocusChange('dateOfBirth', false)}
          dataSet={{ [sardineIdKey]: 'dateOfBirth' }}
          info={{ error: errors?.fieldErrors.dob?.join(', ') }}
          onSubmitEditing={() => ssnInputRef.current?.focus()}
          returnKeyType="next"
          testID={E2E.Config.Ids.Input.DateOfBirth}
        />
        <InputSsn
          value={ssn}
          onChange={(value) => {
            setSsn(value)
            void trackTextChange('ssn', value)
            showErrors && checkForErrors({ ssn: value })
          }}
          onFocus={() => void trackFocusChange('ssn', true)}
          onBlur={() => void trackFocusChange('ssn', false)}
          dataSet={{ [sardineIdKey]: 'ssn' }}
          placeholder="Social security number"
          info={{ error: errors?.fieldErrors.ssn?.join(', ') }}
          ref={ssnInputRef}
          onSubmitEditing={() => totalAnnualIncomeInputRef.current?.focus()}
          returnKeyType="next"
          testID={E2E.Config.Ids.Input.SSN}
        />
        <Input
          sardineId="totalAnnualIncome"
          placeholder="Total annual income"
          value={formatCurrency(totalAnnualIncome, {
            decimals: 0,
            minimumFractionDigits: 0,
          })}
          onChangeText={(text) => {
            setTotalAnnualIncome(text)
            showErrors && checkForErrors({ totalAnnualIncome: text })
          }}
          keyboardType="numeric"
          info={{ error: errors?.fieldErrors.totalAnnualIncome?.join(', ') }}
          ref={totalAnnualIncomeInputRef}
          onSubmitEditing={() => monthlyHousingPaymentInputRef.current?.focus()}
          returnKeyType="next"
          testID={E2E.Config.Ids.Input.TotalAnnualIncome}
        />

        <View>
          <Text style={styles.noteText}>Make sure to factor in:</Text>
        </View>

        <View>
          {[
            'Your yearly salary, wages, commissions, and tips before taxes',
            'Any income from a spouse, partner, family member, or the government that you rely on.',
            'Government benefits, public assistance, retirement benefits, investments and rent paid to you.',
            'Alimony, child support, or separate Maintenance need is not required unless you want it to be considered as a basis for repaying this obligation.',
          ].map((item, index) => (
            <View key={index} style={styles.itemContainer}>
              <View style={styles.bullet} />
              <Text style={[styles.noteText, styles.itemText]}>{item}</Text>
            </View>
          ))}

          <Input
            sardineId="monthlyHousingPayment"
            containerStyle={{ marginTop: 16, marginBottom: 0 }}
            placeholder="Monthly housing payment"
            value={formatCurrency(monthlyHousingPayment, {
              decimals: 0,
              minimumFractionDigits: 0,
            })}
            onChangeText={(text) => {
              setMonthlyHousingPayment(text)
              showErrors && checkForErrors({ monthlyHousingPayment: text })
            }}
            keyboardType="numeric"
            info={{ error: errors?.fieldErrors.monthlyHousingPayment?.join(', ') }}
            ref={monthlyHousingPaymentInputRef}
            returnKeyType="done"
            testID={E2E.Config.Ids.Input.MonthlyHousingPayment}
          />

          <Text style={styles.noteText}>
            This should be your total mortgage payment or whatever you pay in rent for a single
            month. This only includes payments toward housing, not expenses like gas or electric. If
            you don't have a housing payment, you can type in "0."
          </Text>
        </View>
        <View style={{ marginTop: 16 }}>
          <Text>
            <Text style={styles.itemBold}>
              IMPORTANT INFORMATION ABOUT PROCEDURES FOR OPENING A NEW ACCOUNT:{' '}
            </Text>
            <Text style={styles.noteText}>
              To help the government fight the funding of terrorism and money laundering activities,
              Federal law requires all financial institutions to obtain, verify and record
              information that identifies each person who opens an account. What this means for you:
              When you open an account, we will ask for your name, address, date of birth and other
              information that will allow us to identify you. We may also ask to see your driver's
              license or other identifying documents.
            </Text>
          </Text>
        </View>
      </ScrollView>
      <View style={{ marginBottom: 10, marginTop: 10 }}>
        <Button
          title="Continue"
          onPress={onContinuePress}
          testID={E2E.Config.Ids.Press.LastQuestionsContinue}
        />
      </View>
    </CardApplicationWrapper>
  )
}

const noteText = {
  ...typography['paragraph/md'],
  color: mesaColorNames['Greyscale/400 #A4AAAC'],
} satisfies TextProps['style']

const styles = StyleSheet.create({
  noteText,
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  itemBold: {
    ...noteText,
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
  },
  bullet: {
    width: 5,
    height: 5,
    borderRadius: 5,
    backgroundColor: mesaColorNames['Greyscale/400 #A4AAAC'],
    marginLeft: 10,
    marginRight: 5,
    marginTop: 10,
  },
  itemText: { flexShrink: 1 },
})
